import { Box, type CSSProperties } from "@mui/material";
import type { FC, ReactElement, RefObject } from "react";
import type { DeepPartial } from "react-hook-form";
import { notEmpty } from "../../utils/not-empty";
import type { EmailButtonSettings } from "../email-design/types/email-button-settings";
import type { DividerSettings } from "../email-design/types/email-divider-settings";
import type { EmailHeaderSchema } from "../email-design/types/email-header-type";
import {
  type EmailFooterLayoutSettings,
  type EmailLayoutSettings,
} from "../email-design/types/email-layout-type";
import type { EmailTextSettings } from "../email-design/types/email-text-type";
import { makeRenderPaddingPxs } from "../email-design/util";
import {
  type EmailDesignTemplateSettings,
  type SerialisedEditorDesign,
} from "../message/editor/types/serialised-editor-design";
import { useStyleableColumns } from "../message/editor/utils/use-styleable-columns";
import { DividerStyles } from "./divider-styles";
import { EmailButtonStyles } from "./email-button-styles";
import { ReadOnlyEditor } from "./read-only-editor";

const textHeading = ({
  textSettings,
  name,
}: {
  textSettings: DeepPartial<EmailTextSettings>;
  name: "heading1" | "heading2" | "heading3" | "heading4";
}): CSSProperties => {
  const fontFamily = [
    textSettings.header_font,
    textSettings.header_fallback_font,
    textSettings.font,
    textSettings.fallback_font,
  ].filter(notEmpty);

  return {
    color: textSettings[name]?.font_colour,
    fontSize: textSettings[name]?.font_size,
    fontWeight: textSettings[name]?.bold ? "bold" : "normal",
    fontFamily: fontFamily.join(", "),
    lineHeight: textSettings.line_height,
  };
};

export const StyledEditorContainer: FC<{
  width: string;
  settings?: {
    text: DeepPartial<EmailTextSettings> | null;
    layout: DeepPartial<EmailLayoutSettings> | null;
    footer_layout: DeepPartial<EmailFooterLayoutSettings> | null;
    buttons: DeepPartial<EmailButtonSettings> | null;
    dividers: DeepPartial<DividerSettings> | null;
  };
  editorComponent: ReactElement;
  templateSettings?: EmailDesignTemplateSettings;
  footer?: boolean;
}> = ({ width, settings, editorComponent, footer = false }) => {
  const stylableColumns = useStyleableColumns();

  const hSettings = settings?.text
    ? {
        h1: textHeading({ textSettings: settings.text, name: "heading1" }),
        h2: textHeading({ textSettings: settings.text, name: "heading2" }),
        h3: textHeading({ textSettings: settings.text, name: "heading3" }),
        h4: textHeading({ textSettings: settings.text, name: "heading4" }),
      }
    : {};

  const fontFamily = [settings?.text?.font, settings?.text?.fallback_font].filter(notEmpty);

  return (
    <>
      {settings?.buttons && <EmailButtonStyles buttons={settings.buttons} />}
      {settings?.dividers && (
        <DividerStyles dividers={settings.dividers} paper_colour={settings.layout?.paper_colour} />
      )}
      <Box
        sx={{
          width: width,
          margin: "0px auto",
          backgroundColor: settings?.layout?.paper_colour,
          "& .editor-content": {
            color: settings?.text?.font_colour,
            fontSize: settings?.text?.font_size && `${settings?.text?.font_size}px`,
            fontWeight: "normal",
            fontFamily: fontFamily.join(", "),
            lineHeight: settings?.text?.line_height,
            ...hSettings,
            padding: `${(footer ? settings?.footer_layout?.paper_padding_side : settings?.layout?.paper_padding_side) ?? 20}px`,
            paddingTop: `${(footer ? settings?.footer_layout?.paper_padding_top : settings?.layout?.paper_padding_top) ?? 0}px`,
            paddingBottom: `${(footer ? settings?.footer_layout?.paper_padding_bottom : settings?.layout?.paper_padding_bottom) ?? 20}px`,
            ...(footer
              ? {}
              : {
                  border: `${settings?.layout?.border?.width ?? 0}px solid ${
                    settings?.layout?.border?.colour ?? "transparent"
                  }`,
                }),
          },
          "& .editor-content .decorator-block-node:not(.dynamic-toolbar *) p, .editor-content p:not(.dynamic-toolbar *)":
            {
              margin: 0,
              display: "block",
              paddingBottom: `${settings?.layout?.paragraph_padding ?? 0}px`,
              ...(footer ? { margin: "0px" } : {}),
            },
          "& .editor-content .editor-table-cell.data-table-cell > p": {
            paddingBottom: 0,
          },
          "& .editor-content table.editor-table": {
            marginBottom: `${settings?.layout?.paragraph_padding ?? 0}px`,
            borderSpacing: stylableColumns.enabled
              ? 0
              : `${
                  settings?.layout?.column_spacing_type === "spacing"
                    ? settings.layout.column_spacing
                    : 0
                }px`,
            borderCollapse: stylableColumns.enabled
              ? "collapse"
              : settings?.layout?.column_spacing_type === "spacing"
                ? "unset"
                : "collapse",
          },
          "& .editor-content td.editor-table-cell, .editor-table:not(.data-table) tr:hover td.editor-table-cell":
            stylableColumns.enabled
              ? {}
              : {
                  padding: `${
                    settings?.layout?.column_spacing_type === "padding"
                      ? settings.layout.column_padding
                      : 0
                  }px`,
                },
          // un-customised cells will fallback to the global layout settings via css
          "& .editor-content td.editor-table-cell:not(.data-table-cell), .editor-table:not(.data-table) tr:hover td.editor-table-cell:not(.customised-padding)":
            stylableColumns.enabled ? makeRenderPaddingPxs(settings?.layout) : {},
          "& .editor-content a": {
            textDecoration: "none",
            color: settings?.text?.link_color ?? "inherit",
          },
          "& .editor-content a span": {
            textDecoration: "underline",
          },
        }}
      >
        {editorComponent}
      </Box>
    </>
  );
};

export const EmailTemplateLayout: FC<{
  header?: SerialisedEditorDesign;
  headerSettings?: EmailHeaderSchema;
  footer?: SerialisedEditorDesign;
  settings?: {
    text: EmailTextSettings | null;
    layout: EmailLayoutSettings | null;
    footer_layout: EmailFooterLayoutSettings | null;
    buttons: EmailButtonSettings | null;
    dividers: DividerSettings | null;
  };
  editorComponent: ReactElement;
  emailEditorScrollRef: RefObject<HTMLDivElement | null>;
  templateSettings?: EmailDesignTemplateSettings;
}> = ({
  header,
  headerSettings,
  footer,
  settings,
  editorComponent,
  emailEditorScrollRef,
  templateSettings,
}) => {
  const paperWidth = settings?.layout?.max_paper_width
    ? `${settings?.layout?.max_paper_width}px`
    : "100%";

  return (
    <Box
      ref={emailEditorScrollRef}
      sx={{
        p: 0,
        m: 0,
        maxWidth: "100%",
        maxHeight: "80vh",
        overflow: "auto",
        backgroundColor: settings?.layout?.background_colour,
      }}
    >
      <Box>
        {header && (
          <ReadOnlyEditor
            design={header}
            format="email"
            sx={{
              backgroundColor: headerSettings?.backgroundColor,
              maxWidth: "100%",
              width: !settings?.layout?.max_paper_width
                ? "100%"
                : `${settings.layout.max_paper_width}px`,
              paddingRight: `${headerSettings?.paper_padding_side ?? 0}px`,
              paddingLeft: `${headerSettings?.paper_padding_side ?? 0}px`,
              paddingTop: `${headerSettings?.paper_padding_top ?? 0}px`,
              paddingBottom: `${headerSettings?.paper_padding_bottom ?? 0}px`,
            }}
            fullWidth={headerSettings?.fullWidth}
          />
        )}
      </Box>
      <Box>
        <StyledEditorContainer
          width={paperWidth}
          settings={settings}
          editorComponent={editorComponent}
          templateSettings={templateSettings}
        />
        <Box />
        <Box>
          {footer && (
            <StyledEditorContainer
              width={paperWidth}
              settings={settings}
              editorComponent={<ReadOnlyEditor format="email" design={footer} />}
              templateSettings={footer.template}
              footer={true}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
